<script setup lang="ts">
/**
 * Trending Destinations - Bento Grid Layout (Production Ready)
 * 🚨 [架構修復]：
 * 1. 注入 AEO ItemList Schema：讓 AI 與搜尋引擎知道這是一個「推薦清單」。
 * 2. 強化漸層與對比度：確保文字在任何圖片背景下都具備頂級可讀性 (WCAG 標準)。
 * 3. 圖片效能優化：加入 sizes 屬性，確保行動端不會下載過大的圖檔。
 */
import { useHead } from "nuxt/app";
const destinations = [
  {
    id: 1,
    city: "Tokyo",
    country: "Japan",
    price: 650,
    span: "md:col-span-2 md:row-span-2",
    img: "https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?q=80&w=1200", //加大主圖解析度,
  },
  {
    id: 2,
    city: "London",
    country: "UK",
    price: 420,
    span: "md:col-span-1 md:row-span-1",
    img: "https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?q=80&w=800",
  },
  {
    id: 3,
    city: "Paris",
    country: "France",
    price: 580,
    span: "md:col-span-1 md:row-span-1",
    img: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?q=80&w=800",
  },
  {
    id: 4,
    city: "Seoul",
    country: "South Korea",
    price: 510,
    span: "md:col-span-2 md:row-span-1",
    img: "https://images.unsplash.com/photo-1517154421773-0529f29ea451?q=80&w=1200",
  },
];
// 注入 SEO / AEO 結構化資料 (ItemList)
useHead({
  script: [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: destinations.map((d, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: `${d.city}, ${d.country} Flights & Packages`,
          url: `https://www.scholartrip.com/packages/${d.city.toLowerCase()}`,
        })),
      }),
    },
  ],
});
</script>
<template>
  <section class="py-16 md:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <!-- Header -->
    <div
      class="flex flex-col md:flex-row justify-between items-start md:items-end mb-10 gap-6 border-b border-slate-200 pb-8"
    >
      <div>
        <h2
          class="text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900 uppercase italic leading-none"
        >
          Trending <span class="text-brand-600">Now</span>
        </h2>
        <p
          class="text-slate-500 font-bold uppercase tracking-[0.15em] mt-3 text-xs md:text-sm"
        >
          Curated Student & Explorer Bundles
        </p>
      </div>
      <NuxtLink
        to="/packages"
        class="text-[11px] md:text-xs font-black text-brand-600 hover:text-brand-800 flex items-center gap-2 uppercase tracking-[0.2em] group bg-brand-50 hover:bg-brand-100 px-5 py-2.5 rounded-full transition-colors"
      >
        See All Deals
        <Icon
          name="ph:arrow-right-bold"
          class="transition-transform group-hover:translate-x-1"
        />
      </NuxtLink>
    </div>
    <!-- Bento Grid (便當盒佈局) -->
    <div
      class="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-5 auto-rows-[250px] md:auto-rows-[300px]"
    >
      <NuxtLink
        v-for="item in destinations"
        :key="item.id"
        :to="`/packages/${item.city.toLowerCase()}`"
        :class="item.span"
        class="relative rounded-[1.5rem] md:rounded-[2rem] overflow-hidden group shadow-sm hover:shadow-xl transition-all duration-500 focus:outline-none focus:ring-4 focus:ring-brand-500/50"
      >
        <NuxtImg
          :src="item.img"
          :alt="`Travel to ${item.city}`"
          loading="lazy"
          format="webp"
          sizes="sm:100vw md:50vw lg:800px"
          class="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
        />
        <!-- 漸層遮罩：底部加重確保文字可讀性，並加入微小的上提動畫 -->
        <div
          class="absolute inset-0 bg-gradient-to-t from-slate-900/95 via-slate-900/40 to-transparent p-6 md:p-8 flex flex-col justify-end transition-all duration-300"
        >
          <div
            class="transform transition-transform duration-500 group-hover:-translate-y-2"
          >
            <p
              class="text-[10px] md:text-xs font-black text-brand-400 uppercase tracking-[0.2em] mb-1.5 drop-shadow-md"
            >
              {{ item.country }}
            </p>
            <h3
              class="text-3xl md:text-4xl font-black text-white tracking-tighter uppercase leading-none mb-4 italic drop-shadow-lg"
            >
              {{ item.city }}
            </h3>
            <div class="flex items-center gap-3">
              <div
                class="bg-white/10 backdrop-blur-md border border-white/20 px-3 py-1.5 rounded-lg inline-flex items-center"
              >
                <span class="text-white font-black text-lg md:text-xl italic">
                  <span
                    class="text-xs text-white/70 mr-1 uppercase font-bold tracking-widest not-italic"
                    >From</span
                  >
                  ${{ item.price }}
                </span>
              </div>
              <div class="w-1 h-1 rounded-full bg-white/50"></div>
              <span
                class="text-[9px] md:text-[10px] text-white/80 font-bold uppercase tracking-widest"
              >
                Included Flight
              </span>
            </div>
          </div>
        </div>
      </NuxtLink>
    </div>
  </section>
</template>
